// pages/LiveVideo/LiveVideoWatchPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Heart, 
  MessageCircle, 
  Bookmark, 
  Share2, 
  Volume2,
  VolumeX,
  User,
  Calendar,
  Send,
  Eye,
  Users,
  Sparkles
} from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState } from '../../store';
import { uiActions } from '../../store/reducers/uiReducers';
import liveVideoService from '../../services/liveVideoService';
import type { LiveVideo, LiveVideoComment } from '../../services/liveVideoService';
import MainLayout from '../../components/MainLayout/MainLayout';
import { SecureMediaPlayer } from '../../components/SecureMedia';
import VideoSEO from '../../components/SEO/VideoSEO';
import { useVideoSchema } from '../../hooks/useVideoSchema';
import mediaService from '../../services/mediaService';

const LiveVideoWatchPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const isAuthenticated = useSelector((state: RootState) => state.user.isAuthenticated);
  const dispatch = useDispatch();
  
  const [liveVideo, setLiveVideo] = useState<LiveVideo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [comments, setComments] = useState<LiveVideoComment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [isLiked, setIsLiked] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [viewerCount, setViewerCount] = useState(0);
  const [commentCount, setCommentCount] = useState(0);
  
  // Video player state
  const [isMuted, setIsMuted] = useState(false);
  
  // View tracking state
  const [hasRecordedView, setHasRecordedView] = useState(false);
  const [viewId, setViewId] = useState<string | null>(null);
  const [viewStartTime, setViewStartTime] = useState<number | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const pageUrl = `https://strymhub.com/live/${slug}`;

  // Prepare video schema data
  const videoSchemaData = liveVideo ? {
    name: liveVideo.title,
    description: liveVideo.description || liveVideo.short_description || '',
    thumbnailUrl: liveVideo.secure_thumbnail_url || liveVideo.thumbnail || '',
    uploadDate: liveVideo.created_at,
    duration: liveVideo.duration_formatted,
    contentUrl: liveVideo.secure_video_url || liveVideo.video_file || '',
    embedUrl: `https://strymhub.com/live/${slug}/embed`,
    author: {
      name: liveVideo.author_username || 'StrymHub Creator',
      url: `https://strymhub.com/profile/${liveVideo.author_username}`
    },
    publisher: {
      name: 'StrymHub',
      logo: 'https://strymhub.com/android-chrome-512x512.png'
    },
    interactionStatistic: [
      {
        interactionType: 'LikeAction',
        userInteractionCount: likeCount
      },
      {
        interactionType: 'WatchAction', 
        userInteractionCount: liveVideo.total_views || 0
      }
    ]
  } : null;

  // Use video schema hook
  useVideoSchema(videoSchemaData, pageUrl);

  const [showControls] = useState(true);

  const fetchComments = useCallback(async () => {
    try {
      const commentsData = await liveVideoService.getLiveVideoComments(slug!);
      setComments(commentsData.results || []);
    } catch (err) {
      console.warn('Failed to load comments:', err);
    }
  }, [slug]);

  // Record view function
  const recordView = useCallback(async () => {
    if (hasRecordedView || !liveVideo) return null;
    
    try {
      const response = await mediaService.recordView(liveVideo.id);
      if (response.viewId) {
        setViewId(response.viewId);
        setViewStartTime(Date.now());
        setHasRecordedView(true);
        
        // Optimistically update view count in UI
        setLiveVideo(prevVideo => {
          if (prevVideo) {
            return {
              ...prevVideo,
              total_views: (prevVideo.total_views || 0) + 1
            };
          }
          return prevVideo;
        });
      }
      return response.viewId;
    } catch (error) {
      console.error('Failed to record view for live video:', error);
      return null;
    }
  }, [liveVideo, hasRecordedView]);

  const fetchLiveVideo = useCallback(async () => {
    try {
      setLoading(true);
      const video = await liveVideoService.getLiveVideoBySlug(slug!);
      
      if (!video) {
        setError('Live video not found');
        return;
      }

      setLiveVideo(video);
      setLikeCount(video.like_count || 0);
      setViewerCount(video.current_viewers || 0);
      setCommentCount(video.comment_count || 0);
      setIsLiked(video.is_liked || false);
      setIsBookmarked(video.is_bookmarked || false);

      // Fetch comments
      await fetchComments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load live video');
    } finally {
      setLoading(false);
    }
  }, [slug, fetchComments]);

  useEffect(() => {
    if (!slug) {
      setError('Invalid video ID');
      setLoading(false);
      return;
    }

    fetchLiveVideo();
  }, [slug, fetchLiveVideo]);

  // Effect to record view when video starts playing
  useEffect(() => {
    let recordViewTimeout: number;
    
    if (isPlaying && liveVideo && !hasRecordedView) {
      // Record view after 3 seconds of playback
      recordViewTimeout = window.setTimeout(() => {
        recordView();
      }, 3000);
    }
    
    return () => {
      if (recordViewTimeout) {
        window.clearTimeout(recordViewTimeout);
      }
    };
  }, [isPlaying, liveVideo, hasRecordedView, recordView]);

  const handleLike = async () => {
    if (!isAuthenticated) {
      dispatch(uiActions.addNotification({
        type: 'warning',
        message: 'Please login to like videos'
      }));
      return;
    }

    try {
      await liveVideoService.interactWithLiveVideo(slug!, 'like');
      setIsLiked(!isLiked);
      setLikeCount(prev => isLiked ? prev - 1 : prev + 1);
    } catch (_err) {
      dispatch(uiActions.addNotification({
        type: 'error',
        message: 'Failed to update like status'
      }));
    }
  };

  const handleBookmark = async () => {
    if (!isAuthenticated) {
      dispatch(uiActions.addNotification({
        type: 'warning',
        message: 'Please login to bookmark videos'
      }));
      return;
    }

    try {
      await liveVideoService.interactWithLiveVideo(slug!, 'bookmark');
      setIsBookmarked(!isBookmarked);
      dispatch(uiActions.addNotification({
        type: 'success',
        message: isBookmarked ? 'Removed from bookmarks' : 'Added to bookmarks'
      }));
    } catch (_err) {
      dispatch(uiActions.addNotification({
        type: 'error',
        message: 'Failed to update bookmark status'
      }));
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: liveVideo?.title || 'Live Video',
          text: liveVideo?.description || 'Check out this live video!',
          url: pageUrl
        });
      } catch (_err) {
        // Share cancelled
      }
    } else {
      // Fallback to clipboard
      navigator.clipboard.writeText(pageUrl);
      dispatch(uiActions.addNotification({
        type: 'success',
        message: 'Link copied to clipboard!'
      }));
    }
  };

  const handleAddComment = async () => {
    if (!isAuthenticated) {
      dispatch(uiActions.addNotification({
        type: 'warning',
        message: 'Please login to comment'
      }));
      return;
    }

    if (!newComment.trim()) return;

    try {
      await liveVideoService.addLiveVideoComment(slug!, newComment.trim());
      setNewComment('');
      await fetchComments();
      setCommentCount(prev => prev + 1);
    } catch (_err) {
      dispatch(uiActions.addNotification({
        type: 'error',
        message: 'Failed to add comment'
      }));
    }
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const handleLoadedMetadata = () => {
    // Video metadata loaded - can be used for future enhancements
  };

  const getVideoUrl = () => {
    if (!liveVideo) return null;
    return liveVideo.secure_video_url || liveVideo.video_file;
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-black flex items-center justify-center">
          <div className="text-white text-xl">Loading live video...</div>
        </div>
      </MainLayout>
    );
  }

  if (error || !liveVideo) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-black flex items-center justify-center">
          <div className="text-center text-white">
            <h1 className="text-2xl font-bold mb-4">Video Not Found</h1>
            <p className="mb-4">{error || 'The requested live video could not be found.'}</p>
            <button
              onClick={() => navigate(-1)}
              className="text-white hover:text-gray-300"
            >
              ← Go Back
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  const videoUrl = getVideoUrl();

  return (
    <MainLayout>
      {/* SEO Meta Tags */}
      {liveVideo && (
        <VideoSEO
          title={liveVideo.title}
          description={liveVideo.description || `Watch live video: ${liveVideo.title}`}
          videoUrl={liveVideo.secure_video_url || liveVideo.video_file || ''}
          thumbnailUrl={liveVideo.secure_thumbnail_url || 'https://strymhub.com/android-chrome-512x512.png'}
          duration={liveVideo.duration_formatted}
          publishedTime={liveVideo.created_at}
          author={liveVideo.author_username || 'StrymHub Creator'}
          canonicalUrl={pageUrl}
          embedUrl={`https://strymhub.com/live/${slug}/embed`}
        />
      )}
      
      <div className="min-h-screen bg-black">
        {/* Video Player Section */}
        <div className="relative w-full h-screen max-h-[70vh] bg-black">
          {videoUrl ? (
            <>
              <SecureMediaPlayer
                className="w-full h-full object-contain"
                secureVideoUrl={videoUrl}
                controls={false}
                onTimeUpdate={(currentTime) => {
                  // Track playback time for view progress
                  if (currentTime > 0 && !isPlaying) {
                    setIsPlaying(true);
                  }
                }}
                onLoadedData={handleLoadedMetadata}
                onError={() => setError('Failed to load video')}
                disableRightClick={true}
                disableDownload={true}
                watermarkText={userInfo?.firstName || 'User'}
              />

              {/* Video Controls Overlay */}
              <div className={`absolute inset-0 bg-gradient-to-t from-black/60 to-transparent transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0'}`}>
                <div className="absolute top-4 left-4">
                  <button
                    onClick={() => navigate(-1)}
                    className="flex items-center text-white hover:text-gray-300 bg-black/50 backdrop-blur rounded-lg px-3 py-2"
                  >
                    <ArrowLeft className="h-5 w-5 mr-2" />
                    Back
                  </button>
                </div>

                {/* Live Status Badge */}
                {liveVideo.live_status === 'live' && (
                  <div className="absolute top-4 right-4">
                    <div className="inline-flex items-center px-3 py-1 bg-red-500/90 backdrop-blur-md rounded-full">
                      <div className="w-2 h-2 bg-white rounded-full animate-pulse mr-2"></div>
                      <span className="text-sm text-white font-bold">LIVE</span>
                    </div>
                  </div>
                )}

                {/* Bottom Controls */}
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <button
                        onClick={toggleMute}
                        className="p-2 bg-black/50 backdrop-blur rounded-full hover:bg-black/70 transition-colors"
                      >
                        {isMuted ? (
                          <VolumeX className="h-5 w-5 text-white" />
                        ) : (
                          <Volume2 className="h-5 w-5 text-white" />
                        )}
                      </button>
                    </div>

                    <div className="flex items-center space-x-2">
                      <div className="inline-flex items-center px-3 py-1 bg-black/50 backdrop-blur-md rounded-full">
                        <Users className="h-4 w-4 text-white mr-1" />
                        <span className="text-sm text-white font-medium">{viewerCount}</span>
                      </div>
                      
                      {/* Comment Count Button */}
                      <button
                        onClick={() => {
                          // Scroll to comments section
                          document.querySelector('#comments-section')?.scrollIntoView({ behavior: 'smooth' });
                        }}
                        className="relative p-2 bg-black/50 backdrop-blur-md rounded-full hover:bg-black/70 transition-colors"
                      >
                        <MessageCircle className="h-4 w-4 text-white" />
                        {commentCount > 0 && (
                          <div className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                            {commentCount > 99 ? '99+' : commentCount}
                          </div>
                        )}
                      </button>
                      
                      {/* Like Count Button */}
                      <button
                        onClick={handleLike}
                        className={`relative p-2 backdrop-blur-md rounded-full transition-colors ${
                          isLiked 
                            ? 'bg-red-500/80 hover:bg-red-500' 
                            : 'bg-black/50 hover:bg-black/70'
                        }`}
                      >
                        <Heart className={`h-4 w-4 text-white ${isLiked ? 'fill-current' : ''}`} />
                        {likeCount > 0 && (
                          <div className="absolute -top-1 -right-1 bg-red-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                            {likeCount > 99 ? '99+' : likeCount}
                          </div>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-900">
              <div className="text-center text-white">
                <Sparkles className="h-16 w-16 mx-auto mb-4 text-orange-400" />
                <h2 className="text-xl font-bold mb-2">Video not available</h2>
                <p className="text-gray-400">This live video is not currently available for viewing.</p>
              </div>
            </div>
          )}
        </div>

        {/* Video Information & Comments Section */}
        <div className="bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Video Info */}
              <div className="lg:col-span-2">
                <h1 className="text-3xl font-bold text-gray-900 mb-4">{liveVideo.title}</h1>
                
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center text-gray-600">
                      <User className="h-5 w-5 mr-2" />
                      <span>{liveVideo.author_username || 'StrymHub Creator'}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Calendar className="h-5 w-5 mr-2" />
                      <span>{new Date(liveVideo.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Eye className="h-5 w-5 mr-2" />
                      <span>{liveVideo.total_views || 0} views</span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handleLike}
                      className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                        isLiked 
                          ? 'bg-red-600 text-white' 
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      <Heart className={`h-5 w-5 ${isLiked ? 'fill-current' : ''}`} />
                      <span>{likeCount}</span>
                    </button>
                    
                    <button
                      onClick={handleBookmark}
                      className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                        isBookmarked 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      <Bookmark className={`h-5 w-5 ${isBookmarked ? 'fill-current' : ''}`} />
                    </button>
                    
                    <button
                      onClick={handleShare}
                      className="flex items-center space-x-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                      <Share2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                {/* Description */}
                {liveVideo.description && (
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold mb-3">Description</h3>
                    <p className="text-gray-700 whitespace-pre-wrap">{liveVideo.description}</p>
                  </div>
                )}

                {/* Additional Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                  {liveVideo.host_name && (
                    <div>
                      <h4 className="font-semibold text-gray-900">Host</h4>
                      <p className="text-gray-700">{liveVideo.host_name}</p>
                    </div>
                  )}
                  {liveVideo.duration_formatted && (
                    <div>
                      <h4 className="font-semibold text-gray-900">Duration</h4>
                      <p className="text-gray-700">{liveVideo.duration_formatted}</p>
                    </div>
                  )}
                  {liveVideo.tags && (
                    <div className="md:col-span-2">
                      <h4 className="font-semibold text-gray-900 mb-2">Tags</h4>
                      <div className="flex flex-wrap gap-2">
                        {liveVideo.tags.split(',').map((tag, index) => (
                          <span key={index} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                            {tag.trim()}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Comments Section */}
              <div id="comments-section" className="lg:col-span-1">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <MessageCircle className="h-5 w-5 mr-2" />
                    Comments ({commentCount})
                  </h3>

                  {/* Add Comment */}
                  {isAuthenticated ? (
                    <div className="mb-6">
                      <div className="flex space-x-3">
                        <textarea
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          placeholder="Add a comment..."
                          className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                          rows={3}
                        />
                      </div>
                      <div className="flex justify-end mt-2">
                        <button
                          onClick={handleAddComment}
                          disabled={!newComment.trim()}
                          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Send className="h-4 w-4" />
                          <span>Send</span>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                      <p className="text-blue-800">
                        <a href="/login" className="font-semibold hover:underline">Login</a> to join the conversation!
                      </p>
                    </div>
                  )}

                  {/* Comments List */}
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {comments.length > 0 ? (
                      comments.map((comment) => (
                        <div key={comment.id} className="bg-white p-4 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold text-gray-900">{comment.user_username}</span>
                            <span className="text-sm text-gray-500">
                              {new Date(comment.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="text-gray-700">{comment.message}</p>
                        </div>
                      ))
                    ) : (
                      <div className="text-center text-gray-500 py-8">
                        <MessageCircle className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                        <p>No comments yet. Be the first to comment!</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default LiveVideoWatchPage;